import { useState, useMemo, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FilterSidebar, { Filters } from './components/FilterSidebar';
import RestaurantCard from './components/RestaurantCard';
import SeoJsonLd from '../../components/feature/SeoJsonLd';
import { supabase } from '../../lib/supabase';

const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommandés' },
  { value: 'rating', label: 'Mieux notés' },
  { value: 'distance', label: 'Plus proches' },
  { value: 'delivery', label: 'Livraison rapide' },
  { value: 'price_asc', label: 'Prix croissant' },
  { value: 'price_desc', label: 'Prix décroissant' },
];

const DEFAULT_FILTERS: Filters = {
  categories: [],
  minRating: 0,
  maxDistance: 10,
  priceRanges: [],
  onlyNew: false,
  onlyDiscount: false,
};

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const restaurantsPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Restaurants – Localygo',
  url: `${SITE_URL}/restaurants`,
  description: 'Trouvez votre restaurant idéal parmi plus de 50 établissements. Filtrez par cuisine, note, distance et prix.',
  provider: {
    '@type': 'Organization',
    name: 'Localygo',
    url: SITE_URL,
  },
};

interface Restaurant {
  id: string;
  name: string;
  cuisine: string;
  category: string;
  rating: number;
  reviewCount: number;
  deliveryTime: string;
  deliveryFee: string;
  distance: number;
  priceRange: number;
  image: string;
  isNew: boolean;
  discount: number | null;
  specialties: string[];
}

export default function RestaurantsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState('recommended');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRestaurants = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('restaurants')
          .select('*')
          .eq('is_active', true)
          .order('rating', { ascending: false });

        if (error) throw error;

        const mapped: Restaurant[] = (data || []).map((r: any) => ({
          id: r.id,
          name: r.name || 'Restaurant',
          cuisine: r.cuisine_type || 'Cuisine variée',
          category: r.cuisine_type || 'Autre',
          rating: Number(r.rating) || 4.0,
          reviewCount: r.total_reviews || 0,
          deliveryTime: r.delivery_time || '30-40 min',
          deliveryFee: r.delivery_fee ? `${Number(r.delivery_fee).toFixed(2)} €` : 'Gratuit',
          distance: r.latitude && r.longitude ? Math.random() * 5 : 2.5,
          priceRange: r.price_range === '€€€' ? 3 : r.price_range === '€€' ? 2 : 1,
          image: r.image_url || `https://public.readdy.ai/ai/img_res/c84f8f92f6983e65e57f22047069105b.jpg$%7Br.cuisine_type%20%7C%7C%20food%7D%20restaurant%20dish%20professional%20food%20photography%20warm%20lighting%20appetizing%20presentation%20clean%20simple%20background&width=800&height=500&seq=rest-${r.id}&orientation=landscape`,
          isNew: r.created_at ? new Date(r.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) : false,
          discount: r.is_featured ? 15 : null,
          specialties: r.description ? [r.description.slice(0, 30)] : [],
        }));

        setRestaurants(mapped);
      } catch (err) {
        console.error('Erreur lors du chargement des restaurants:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurants();

    const channel = supabase
      .channel('restaurants-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'restaurants' }, () => {
        fetchRestaurants();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = useMemo(() => {
    let result = [...restaurants];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        r =>
          r.name.toLowerCase().includes(q) ||
          r.cuisine.toLowerCase().includes(q) ||
          r.category.toLowerCase().includes(q) ||
          r.specialties.some(s => s.toLowerCase().includes(q))
      );
    }

    if (filters.categories.length > 0) {
      result = result.filter(r => filters.categories.includes(r.category));
    }

    if (filters.minRating > 0) {
      result = result.filter(r => r.rating >= filters.minRating);
    }

    if (filters.maxDistance < 10) {
      result = result.filter(r => r.distance <= filters.maxDistance);
    }

    if (filters.priceRanges.length > 0) {
      result = result.filter(r => filters.priceRanges.includes(r.priceRange));
    }

    if (filters.onlyNew) {
      result = result.filter(r => r.isNew);
    }

    if (filters.onlyDiscount) {
      result = result.filter(r => r.discount !== null);
    }

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'distance':
        result.sort((a, b) => a.distance - b.distance);
        break;
      case 'delivery':
        result.sort((a, b) => {
          const aMin = parseInt(a.deliveryTime.split('-')[0]);
          const bMin = parseInt(b.deliveryTime.split('-')[0]);
          return aMin - bMin;
        });
        break;
      case 'price_asc':
        result.sort((a, b) => a.priceRange - b.priceRange);
        break;
      case 'price_desc':
        result.sort((a, b) => b.priceRange - a.priceRange);
        break;
      default:
        break;
    }

    return result;
  }, [searchQuery, filters, sortBy, restaurants]);

  const activeFilterCount =
    filters.categories.length +
    filters.priceRanges.length +
    (filters.minRating > 0 ? 1 : 0) +
    (filters.maxDistance < 10 ? 1 : 0) +
    (filters.onlyNew ? 1 : 0) +
    (filters.onlyDiscount ? 1 : 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoJsonLd data={restaurantsPageSchema} id="schema-restaurants" />
      <Navbar />

      {/* Hero */}
      <div
        className="relative h-72 flex items-center justify-center"
        style={{
          backgroundImage:
            'url(https://readdy.ai/api/search-image?query=vibrant%20colorful%20food%20market%20street%20scene%20with%20diverse%20cuisine%20stalls%20warm%20evening%20lighting%20bokeh%20effect%20artistic%20overhead%20view%20of%20various%20dishes%20from%20different%20cultures%20rich%20textures%20appetizing%20food%20photography&width=1600&height=600&seq=rest-hero-bg&orientation=landscape)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Trouvez votre restaurant idéal
          </h1>
          <p className="text-white/80 text-base mb-7">
            Filtrez par cuisine, note, distance et prix pour commander ce qui vous fait envie
          </p>
          <div className="relative max-w-xl mx-auto">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-lg"></i>
            </div>
            <input
              type="text"
              placeholder="Restaurant, cuisine, plat..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 shadow-lg"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            {/* Mobile filter button */}
            <button
              onClick={() => setIsMobileFilterOpen(true)}
              className="lg:hidden flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-teal-400 transition-colors cursor-pointer whitespace-nowrap shadow-sm"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-equalizer-2-line text-teal-600"></i>
              </div>
              Filtres
              {activeFilterCount > 0 && (
                <span className="bg-teal-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <p className="text-sm text-gray-500">
              <span className="font-bold text-gray-900">{filtered.length}</span>{' '}
              restaurant{filtered.length > 1 ? 's' : ''} trouvé{filtered.length > 1 ? 's' : ''}
            </p>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-sort-desc text-gray-400 text-sm"></i>
            </div>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white cursor-pointer text-gray-700 shadow-sm"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-5">
            {filters.categories.map(cat => (
              <span
                key={cat}
                className="flex items-center gap-1.5 bg-teal-50 text-teal-700 border border-teal-200 px-3 py-1 rounded-full text-xs font-medium"
              >
                {cat}
                <button
                  onClick={() =>
                    setFilters(f => ({ ...f, categories: f.categories.filter(c => c !== cat) }))
                  }
                  className="w-3.5 h-3.5 flex items-center justify-center hover:text-teal-900 cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </span>
            ))}
            {filters.priceRanges.map(p => (
              <span
                key={p}
                className="flex items-center gap-1.5 bg-teal-50 text-teal-700 border border-teal-200 px-3 py-1 rounded-full text-xs font-medium"
              >
                {['€', '€€', '€€€'][p - 1]}
                <button
                  onClick={() =>
                    setFilters(f => ({ ...f, priceRanges: f.priceRanges.filter(x => x !== p) }))
                  }
                  className="w-3.5 h-3.5 flex items-center justify-center hover:text-teal-900 cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </span>
            ))}
            {filters.minRating > 0 && (
              <span className="flex items-center gap-1.5 bg-teal-50 text-teal-700 border border-teal-200 px-3 py-1 rounded-full text-xs font-medium">
                ★ {filters.minRating}+
                <button
                  onClick={() => setFilters(f => ({ ...f, minRating: 0 }))}
                  className="w-3.5 h-3.5 flex items-center justify-center hover:text-teal-900 cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </span>
            )}
            {filters.maxDistance < 10 && (
              <span className="flex items-center gap-1.5 bg-teal-50 text-teal-700 border border-teal-200 px-3 py-1 rounded-full text-xs font-medium">
                &lt; {filters.maxDistance} km
                <button
                  onClick={() => setFilters(f => ({ ...f, maxDistance: 10 }))}
                  className="w-3.5 h-3.5 flex items-center justify-center hover:text-teal-900 cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </span>
            )}
            {filters.onlyNew && (
              <span className="flex items-center gap-1.5 bg-teal-50 text-teal-700 border border-teal-200 px-3 py-1 rounded-full text-xs font-medium">
                Nouveaux
                <button
                  onClick={() => setFilters(f => ({ ...f, onlyNew: false }))}
                  className="w-3.5 h-3.5 flex items-center justify-center hover:text-teal-900 cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </span>
            )}
            {filters.onlyDiscount && (
              <span className="flex items-center gap-1.5 bg-teal-50 text-teal-700 border border-teal-200 px-3 py-1 rounded-full text-xs font-medium">
                Promotions
                <button
                  onClick={() => setFilters(f => ({ ...f, onlyDiscount: false }))}
                  className="w-3.5 h-3.5 flex items-center justify-center hover:text-teal-900 cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </span>
            )}
            <button
              onClick={() => setFilters(DEFAULT_FILTERS)}
              className="text-xs text-gray-500 hover:text-red-500 underline cursor-pointer whitespace-nowrap"
            >
              Tout effacer
            </button>
          </div>
        )}

        {/* Layout: sidebar + grid */}
        <div className="flex gap-8">
          <FilterSidebar
            filters={filters}
            onChange={setFilters}
            onReset={() => setFilters(DEFAULT_FILTERS)}
            isOpen={isMobileFilterOpen}
            onClose={() => setIsMobileFilterOpen(false)}
          />

          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24">
                <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 text-sm">Chargement des restaurants...</p>
              </div>
            ) : filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map(r => (
                  <RestaurantCard key={r.id} restaurant={r} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mb-5">
                  <i className="ri-restaurant-line text-4xl text-gray-300"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Aucun restaurant trouvé</h3>
                <p className="text-gray-500 text-sm mb-5 max-w-xs">
                  Essayez de modifier vos filtres ou votre recherche pour voir plus de résultats.
                </p>
                <button
                  onClick={() => {
                    setFilters(DEFAULT_FILTERS);
                    setSearchQuery('');
                  }}
                  className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
